// Cambiar la voz de un clip de VIDEO (barra IA del editor).
// POST al atlas-backend `/v1/videos/change-voice`: el backend extrae el audio,
// lo pasa por S2S (misma voz elegida) y re-muxea el audio nuevo al video. Es
// inline-wait (el backend espera hasta ~4,7 min); devuelve el `path` del video
// nuevo en `video-project-assets`. Espeja el contrato de `videoCreator.ts`.

import { atlasBackendFetch, pollJobUntilSettled } from './atlasBackend';
import {
  GenerateAdError,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from './generateAdRequest';

const CHANGE_VOICE_TIMEOUT_MS = 300_000;
// Tope del poll post-202: el job de voz comparte la cola `video` con jobs
// largos (upscale, template…), así que puede tardar en arrancar.
const CHANGE_VOICE_PENDING_POLL_MS = 15 * 60 * 1000;

export async function changeClipVoiceVideo(params: {
  clipUrl: string;
  voiceId: string;
  durationSec: number;
  projectId: string | null;
}): Promise<{ path: string }> {
  let res: Response;
  try {
    res = await atlasBackendFetch(
      '/v1/videos/change-voice',
      {
        clip_url: params.clipUrl,
        voz_id: params.voiceId,
        duration_sec: params.durationSec,
        project_id: params.projectId,
      },
      { signal: AbortSignal.timeout(CHANGE_VOICE_TIMEOUT_MS) },
    );
  } catch (e: any) {
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      throw new Error('El cambio de voz tardó demasiado. Probá de nuevo.');
    }
    throw new Error('No se pudo conectar. Revisá tu conexión.');
  }

  const rawText = await res.text().catch(() => '');
  let json: any = null;
  if (rawText.trim()) {
    try {
      json = JSON.parse(rawText);
    } catch {
      /* respuesta no-JSON → cae al error genérico de abajo */
    }
  }

  if (res.status === 402) {
    throw new GenerateAdError(
      'billing',
      json?.error || 'No tenés créditos suficientes para cambiar la voz.',
      'billing',
    );
  }
  // 202 = el wait del backend venció con el job todavía corriendo. CLAVE:
  // hay que poolear hasta que termine (el job de fondo igual sube el video y
  // COBRA). Si acá abandonáramos, el usuario pagaría y nunca recibiría el clip
  // — y reintentar crearía otra ejecución que cobra de nuevo (doble cobro).
  if (res.status === 202 && json?.job_id) {
    const settled = await pollJobUntilSettled(String(json.job_id), {
      timeoutMs: CHANGE_VOICE_PENDING_POLL_MS,
    });
    if (settled?.status === 'succeeded') {
      const p = (settled.output as any)?.path;
      if (typeof p === 'string' && p) return { path: p };
    }
    if (settled?.status === 'failed') {
      const t = (settled.errorType as GenerateAdErrorType) || 'technical';
      throw new GenerateAdError(
        t,
        settled.userMessage ?? mapErrorTypeToMessage(t),
        settled.errorType ?? null,
      );
    }
    throw new Error('El cambio de voz tardó demasiado. Probá de nuevo.');
  }
  if (!res.ok || json?.response !== 'success' || !json?.path) {
    const t = (json?.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(t, json?.error || mapErrorTypeToMessage(t), json?.error_type ?? null);
  }

  return { path: json.path as string };
}
